/* eslint-disable */

import { IconButton, Menu } from "@mui/material";
import React, { Suspense, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import "./corevebCardv2.css";
import PremiumOverviewContainer from "../PremiumOverviewV2/PremiumOverviewV2";
import { CorePanhandlePlansName, CorePlansName, VEB_PLAN_ARRAY } from "../../../../constants/plan";
import DependentMenuItems from "../menuItemsv2/DependentMenuItems";
import RidersMenuItems from "../menuItemsv2/RidersMenuItems";
import EnrollmentIInfoGrid from "../enrollmentInfoGridv2/EnrollmentIInfoGrid";
import { coreVEBBenefitsProps } from "../../../../types/enrollmentV2.types";
import BeneficiaryMenuItems from "../menuItemsv2/BeneficiaryMenuItems";
import { formatTwoDecimalPlaces } from "../../../../utils/commonFunctions/decimal";
import { GROUP_NUMBER } from "../../../../constants/groups";
import CustomModal from "../../customModal/CustomModal";
import EditBeneficiaryForm from "./editBeneficiaryForm/EditBeneficiaryForm";
import { useGetActiveVEBPlansQuery } from "../../../../features/VEBPlans/vebPlansApi";
import { AuthContext } from "../../../../contexts";
import { generateFileURL } from "../../../../utils/commonFunctions/generateFileURL";
import { LazyPlanBranding } from "../..";
import { api } from "../../../../utils/api";
import { trackPromise } from "react-promise-tracker";

const CoreVebCard: React.FC<coreVEBBenefitsProps> = ({
	backgroundImage,
	plan_code,
	plan_name,
	base_plan_name,
	enrollment_status,
	total_premium,
	effective_date,
	benefit_end_date,
	termination_date,
	enrollment_date,
	initial_effective_date,
	enrollments,
	coreTrueCount,
	vebTrueCount,
	coreDependents,
	vebDependents,
	vebRiderCount,
	vebRiders,
	sub_plan,
	corePlans, // ← from parent, replaces internal API call
	currentPlans, // ← from parent, replaces useGetActiveVEBPlansQuery
	futurePlans // ← from parent, replaces useGetActiveVEBPlansQuery
}) => {
	const systemDate = new Date();
	const effectiveDate = effective_date ? new Date(effective_date) : null;
	const computedStatus = effectiveDate && effectiveDate > systemDate ? "FUTURE" : enrollment_status || "N/A";

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [overallPremiumAnchorEl, setOverallPremiumAnchorEl] = useState<null | HTMLElement>(null);
	const [noDependentsAnchorEl, setNoDependentsAnchorEl] = useState<null | HTMLElement>(null);
	const [vebRiderMenuAnchorEl, setVebRiderMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [beneficiaryMenuAnchorEl, setBeneficiaryMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [editBeneficiaryModalOpen, setEditBeneficiaryModalOpen] = useState<boolean>(false);

	const { member } = useContext(AuthContext);

	// ─── Derived booleans ─────────────────────────────────────────────────────
	// const isCorePlan = useMemo(() => CorePlansName.includes(plan_name), [plan_name]);
	const isCorePlan = useMemo(() => {
		if (!corePlans?.length) return false;

		return corePlans.some((plan) => plan.plan_name === plan_name);
	}, [corePlans, plan_name]);

	const isPanhandle = useMemo(
		() => enrollments.length > 0 && enrollments[0].group_number === GROUP_NUMBER.BOP_PANHANDLE_FIRST_BANK,
		[enrollments]
	);

	const isProgressiveLab = useMemo(
		() => enrollments.length > 0 && enrollments[0].group_number === GROUP_NUMBER.PROGRESSIVE_LABORATORIES,
		[enrollments]
	);

	// ─── Menu open states ─────────────────────────────────────────────────────
	const open = Boolean(anchorEl);
	const overallPremiumOpenStatus = Boolean(overallPremiumAnchorEl);
	const beneficiaryMenuOpenStatus = Boolean(beneficiaryMenuAnchorEl);

	// ─── Plan selectors — now use props directly, no API call needed ──────────
	const selectCorePlan = useMemo(
		() => corePlans.find((plan) => plan.plan_name === plan_name) as any,
		[corePlans, plan_name]
	);
	console.log("this is the nerollment", enrollments);
	const selectedVEBPlan = useMemo(
		() =>
			currentPlans.find((plan) => plan.plan_name === plan_name && plan.plan_code === plan_code) ||
			futurePlans.find((plan) => plan.plan_name === plan_name && plan.plan_code === plan_code),
		[currentPlans, futurePlans, plan_name, plan_code]
	);

	// ─── Active plan design (core vs VEB) ────────────────────────────────────
	const activePlanDesign = useMemo(
		() => (isCorePlan ? selectCorePlan?.planDesign : selectedVEBPlan?.planDesign),
		[isCorePlan, selectCorePlan, selectedVEBPlan]
	);

	const isBeneficiaryRequired = useMemo(
		() =>
			isCorePlan
				? selectCorePlan?.beneficiaries_required ?? false
				: selectedVEBPlan?.beneficiaries_required ?? false,
		[isCorePlan, selectCorePlan, selectedVEBPlan]
	);

	// ─── Handlers ────────────────────────────────────────────────────────────
	const handleOpenCloseEditBeneficiaryModal = useCallback(() => {
		setEditBeneficiaryModalOpen((prev) => !prev);
	}, []);

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			if (isCorePlan) {
				if (enrollments.length > 1 && coreTrueCount[plan_code + "" + effective_date] >= 1) {
					setIsOpen((prev) => !prev);
					setAnchorEl(event.currentTarget);
				} else {
					setNoDependentsAnchorEl(event.currentTarget);
				}
			} else {
				if (enrollments.length > 1 && vebTrueCount[plan_code] >= 1) {
					setIsOpen((prev) => !prev);
					setAnchorEl(event.currentTarget);
				} else {
					setNoDependentsAnchorEl(event.currentTarget);
				}
			}
		},
		[isCorePlan, enrollments.length, coreTrueCount, vebTrueCount, plan_code, effective_date]
	);

	const handleOpenPremiumClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setOverallPremiumAnchorEl(event.currentTarget);
	}, []);

	const handleVebRiderMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setVebRiderMenuAnchorEl(event.currentTarget);
	}, []);

	const handleVebRiderMenuClose = useCallback(() => setVebRiderMenuAnchorEl(null), []);

	const handleBeneficiaryMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setBeneficiaryMenuAnchorEl(event.currentTarget);
	}, []);

	const handleBeneficiaryMenuClose = useCallback(() => setBeneficiaryMenuAnchorEl(null), []);

	const handleClosePremiumPop = useCallback(() => setOverallPremiumAnchorEl(null), []);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
		setIsOpen(false);
		setNoDependentsAnchorEl(null);
	}, []);

	// ─── Dependents ──────────────────────────────────────────────────────────
	const getDependentsDetails = useCallback((key: string, dependents: any): any[] => dependents[key] || [], []);

	const specificDependents = useMemo(
		() =>
			isCorePlan
				? getDependentsDetails(plan_code + "" + effective_date, coreDependents)
				: getDependentsDetails(plan_code, vebDependents),
		[isCorePlan, plan_code, effective_date, coreDependents, vebDependents, getDependentsDetails]
	);

	const dependentProgressiveLabItem = useMemo(
		() =>
			specificDependents
				?.filter((dependent: any) => dependent.plan_code === plan_code)
				.map((dependent: any) => ({
					name: dependent.member.first_name + " " + dependent.member.last_name,
					label: dependent.member.relationship
				})),
		[specificDependents, plan_code]
	);

	// ─── Riders ──────────────────────────────────────────────────────────────
	const riderItem = useMemo(
		() =>
			vebRiders[plan_code]?.map((rider: any) => ({
				name: rider.member.first_name + " " + rider.member.last_name,
				riderName: rider.plan_name,
				label: rider.member.relationship,
				amount: `$${rider.premium_amount}`
			})),
		[vebRiders, plan_code]
	);

	// ─── Beneficiaries ────────────────────────────────────────────────────────
	const specificBeneficiaries = useMemo(
		() =>
			enrollments
				.filter((enrollment) => enrollment.VEB_primary_beneficiary || enrollment.primary_beneficiery)
				.flatMap((enrollment) => {
					const beneficiaries = enrollment.VEB_primary_beneficiary || enrollment.primary_beneficiery || [];
					return beneficiaries.map((beneficiary: any) => ({
						name: beneficiary.first_name
							? beneficiary.first_name + " " + beneficiary.last_name
							: beneficiary.name,
						relationship: beneficiary.relationship,
						percentage: `${beneficiary.percentage}%`,
						type: beneficiary.type || "Primary"
					}));
				}),
		[enrollments]
	);

	// ─── Premium details (static) ─────────────────────────────────────────────
	const premiumDetails = useMemo(
		() => ({
			health_plan: [
				{ type: "Standard", premium_amount: 60 },
				{ type: "Child", premium_amount: 72 },
				{ type: "Spouse", premium_amount: 55 }
			]
		}),
		[]
	);

	// ─── Render ───────────────────────────────────────────────────────────────
	return (
		<div className="inner-card-info">
			{/* ── Image + Ribbon ── */}
			<div className="image-ribbion-container" style={{ position: "relative" }}>
				<div
					style={{
						width: "290px",
						height: "200px",
						overflow: "hidden",
						borderRadius: "8px",
						boxShadow: "0 4px 14px rgba(0,0,0,0.35)"
					}}
				>
					{activePlanDesign?.branding_design?.image && (
						<LazyPlanBranding imgSrc={generateFileURL(activePlanDesign.branding_design.image)} />
					)}
				</div>

				{activePlanDesign?.branding_design?.logo && (
					<div
						style={{
							position: "absolute",
							top: "10px",
							left: "8px",
							width: "200px",
							height: "40px",
							overflow: "hidden",
							marginTop: "-60px",
							borderRadius: "6px",
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-start",
							padding: "4px",
							zIndex: 2
						}}
					>
						<div style={{ width: "160px", height: "56px", overflow: "hidden" }}>
							<img
								src={generateFileURL(activePlanDesign.branding_design.logo)}
								alt="Brand logo"
								style={{
									width: "100%",
									height: "100%",
									objectFit: "contain",
									objectPosition: "left"
								}}
							/>
						</div>
					</div>
				)}

				<span
					className="sponsored-ribbon"
					style={
						isCorePlan
							? { background: "linear-gradient(to right, #f40752, #f9ab8f)" }
							: { background: "linear-gradient(to right, #8a2be2, #d8bfd8)" }
					}
				>
					{isCorePlan ? "Core Plan" : "Veb Plan"}
					<div className="glows"></div>
				</span>
			</div>

			{isBeneficiaryRequired && (
				<Suspense fallback={<div />}>
					<CustomModal open={editBeneficiaryModalOpen} onClose={handleOpenCloseEditBeneficiaryModal} title="">
						<EditBeneficiaryForm
							handleOpenCloseEditBeneficiaryModal={handleOpenCloseEditBeneficiaryModal}
							plan_type={isCorePlan ? "core" : "VEB"}
							plan_code={plan_code}
							plan_name={plan_name}
							base_plan_name={base_plan_name}
							enrollments={enrollments}
						/>
					</CustomModal>
				</Suspense>
			)}

			{/* ── Plan Info ── */}
			<div style={{ flex: "1" }}>
				<div className="inner-card-info">
					<h2 style={{ fontWeight: "bold", fontSize: "23px", marginBottom: "3px", marginTop: "3px" }}>
						📌{plan_name}
						{/* <span
							className="active-ribbon"
							style={{
								backgroundColor:
									enrollment_status === "SUBMITTED"
										? "#0891b2"
										: enrollment_status === "WAITING"
										? "#f59e0b"
										: enrollment_status === "ACTIVE"
										? "green"
										: ""
							}}
						>
							{enrollment_status}
						</span> */}
						<span
							className="active-ribbon"
							style={{
								backgroundColor:
									computedStatus === "SUBMITTED"
										? "#0891b2"
										: computedStatus === "WAITING"
										? "#f59e0b"
										: computedStatus === "ACTIVE"
										? "green"
										: computedStatus === "FUTURE"
										? "#c2410c"
										: ""
							}}
						>
							{computedStatus}
						</span>
						<span
							style={{
								display: "block",
								color: "red",
								fontWeight: "bold",
								width: "fit-content",
								fontSize: "15px",
								marginLeft: "30px"
							}}
						>
							{plan_code}
						</span>
					</h2>

					<div style={{ display: "flex", alignItems: "center" }}>
						<div>
							<p>
								Total Premium
								{isPanhandle || isProgressiveLab ? (
									<span style={{ padding: "0 8px" }}>:</span>
								) : (
									<IconButton onClick={handleOpenPremiumClick}>
										<FaInfoCircle size={18} />
									</IconButton>
								)}
							</p>
							<PremiumOverviewContainer
								open={overallPremiumOpenStatus}
								anchorEl={overallPremiumAnchorEl}
								handleClose={handleClosePremiumPop}
								premiumDetails={premiumDetails}
								enrollment={enrollments}
							/>
						</div>
						<span style={{ fontSize: "26px", color: "green", fontWeight: "1000" }}>
							${formatTwoDecimalPlaces(total_premium)}
						</span>

						<div style={{ marginLeft: 20 }}>
							{dependentProgressiveLabItem && (
								<Menu
									id="basic-menu"
									anchorEl={anchorEl}
									open={open}
									onClose={handleClose}
									MenuListProps={{ "aria-labelledby": "basic-button" }}
								>
									<DependentMenuItems items={dependentProgressiveLabItem} />
								</Menu>
							)}
							{vebRiders && vebRiders[plan_code] && (
								<Menu
									anchorEl={vebRiderMenuAnchorEl}
									open={Boolean(vebRiderMenuAnchorEl)}
									onClose={handleVebRiderMenuClose}
								>
									<RidersMenuItems items={riderItem} />
								</Menu>
							)}
							<Menu
								anchorEl={beneficiaryMenuAnchorEl}
								open={beneficiaryMenuOpenStatus}
								onClose={handleBeneficiaryMenuClose}
							>
								<BeneficiaryMenuItems items={specificBeneficiaries} />
							</Menu>
						</div>
					</div>
				</div>

				<EnrollmentIInfoGrid
					handleOpenCloseEditBeneficiaryModal={handleOpenCloseEditBeneficiaryModal}
					plan_code={plan_code}
					plan_name={plan_name}
					base_plan_name={base_plan_name}
					sub_plan={sub_plan}
					enrollment_date={enrollment_date}
					initial_effective_date={initial_effective_date}
					effective_date={effective_date}
					benefit_end_date={benefit_end_date}
					termination_date={termination_date}
					beneficiaries_required={isBeneficiaryRequired}
					coreDependentsCount={coreTrueCount[plan_code + "" + effective_date]}
					vebDependentsCount={vebTrueCount[plan_code]}
					coreTrueCount={coreTrueCount[plan_code + "" + effective_date]}
					vebTrueCount={vebTrueCount[plan_code]}
					vebRiderCount={vebRiderCount[plan_code]}
					handleClick={handleClick}
					handleVebRiderMenuClick={handleVebRiderMenuClick}
					enrollmentsLength={enrollments.length}
					enrollments={enrollments}
					handleVebBeneficiaryMenuClick={handleBeneficiaryMenuClick}
					currentPlans={currentPlans} // ← pass to child, removes its query
					futurePlans={futurePlans} // ← pass to child, removes its query
					corePlans={corePlans} // ← pass to child, removes its query
				/>
			</div>
		</div>
	);
};

export default CoreVebCard;
